<template>
  <el-container class="base-container">
    <FaqPanel
      page="marketplaces"
      :top-offset="50"
      :max-width="350"
      :backdrop="true"
    />
    <el-main class="p-none">
      <Sticky
        :z-index="10"
        :sticky-top="50"
        :disabled="!isDesktop"
        class-name="filter-panel"
      >
        <FilterPanel v-model="filter" @refresh="update({})" />
      </Sticky>
      <MarketplaceTable
        :items="items"
        :total="total"
        :sum="sum"
        :loading="loading"
        :show-size-column="showSizeColumn"
        :filters="filter"
        :list-button-state="listButtonState"
        class="mt-1"
        @show-chat="toggleChat"
        @show-campaign-window="toggleCampaignWindow"
        @show-deal-form="toggleDealForm"
        @sort="applySorting"
        @download="download"
        @openModal="openModal"
      />
      <Pagination
        class="mt-1"
        :total="total"
        :page="page"
        @pagination="update"
      />
    </el-main>
    <RequestDealModal
      :id="receiverId"
      :fields="offerDealData"
      :visible="!!receiverId"
      @close="toggleChat"
    />
    <CampaignWindow
      v-if="platformId"
      :id="platformId"
      :visible="!!platformId"
      @close="toggleCampaignWindow"
      @update="goToCampaigns"
    />
    <SpotListModal
      v-model="showModal"
      @save="onSubmit"
      @close="
        () => {
          showModal = false
        }
      "
    />
  </el-container>
</template>
<script lang="ts">
import { computed, ref, reactive, defineComponent, watch, h } from 'vue'
import { Stream, AdType, ISorter } from '@/api/types'

import {
  IMarketplaceStatFilter,
  IMarketplaceStatPayload
} from '@/apps/traforama/interfaces/marketplaceStat'

import RequestDealModal from '@/apps/traforama/views/flat_deals/components/RequestDealModal.vue'
import CampaignWindow from './components/CampaignWindow.vue'
import Pagination from '@/components/Pagination.vue'
import MarketplaceTable from './components/MarketplaceTable.vue'
import FilterPanel from './components/FilterPanel.vue'
import SpotListModal from './components/SpotListModal.vue'
import { useSpotsStore } from '@/apps/traforama/store/spots'
import { useMarketplaceStore } from '@/apps/traforama/store/marketplace'
import { useCountriesStore } from '@/store/modules/countries'
import { exportJson2Excel, exportCSVFile } from '@/utils/excel'
import { formatJson } from '@/utils'
import Sticky from '@/components/Sticky.vue'
import { LIMIT } from '@/constants'
import useRouter from '@/hooks/useRouter'
import useResize from '@/hooks/useResize'
import useQueryFilters from '@/hooks/useQueryFilters'
import FaqPanel from '@/components/FaqPanel/Index.vue'
import { ElMessage, ElLink } from 'element-plus'
import { safeSubmitProcessing } from '@/utils/helpers'
import useValidation from '@/hooks/useValidation'
import { SpotsListValidationErrorsKey } from '@/api/types'

const CSV_HEADERS = [
  'spot_id',
  'domain',
  'name',
  'quality_title',
  'rank',
  'stream',
  'traffic',
  'external',
  'availability'
]

const SORT_ALIAS: { [key: string]: any } = {
  ascending: 'asc',
  descending: 'desc'
}
const SORT_PROP_ALIAS: { [key: string]: any } = {
  domain: 'domain',
  quality: 'quality',
  traffic: 'hits',
  name: 'sid',
  rank: 'rank'
}

export default defineComponent({
  name: 'Marketplace',
  components: {
    RequestDealModal,
    Pagination,
    FilterPanel,
    MarketplaceTable,
    Sticky,
    CampaignWindow,
    FaqPanel,
    SpotListModal
  },
  setup(_props, context) {
    const marketplaceStore = useMarketplaceStore()
    const spotsStore = useSpotsStore()
    const countriesStore = useCountriesStore()

    countriesStore.GetItems()

    const loading = computed(
      () =>
        Object.keys(marketplaceStore.itemsLoading).length > 0 ||
        marketplaceStore.listLoading
    )
    const page = computed(() => marketplaceStore.currentPage)
    const items = computed(() => marketplaceStore.currentItems)
    const total = computed(() => marketplaceStore.total)
    const sum = computed(() => marketplaceStore.sum)
    const limit = ref(LIMIT)
    const filter = computed({
      get: () => marketplaceStore.filter,
      set: (val: IMarketplaceStatFilter) => marketplaceStore.SetFilters(val)
    })

    const receiverId = ref('')
    const platformId = ref('')
    const showModal = ref(false)

    const { router, route } = useRouter()
    const { isDesktop } = useResize()

    const { getQueryFilters } = useQueryFilters({
      device_ids: 'array-int',
      countries_ids: 'array-str'
    })

    const offerDealData = ref({})

    const sorter = reactive<ISorter>({
      direction: 'desc',
      field: 'hits'
    })
    marketplaceStore.SetSortField('hits')

    const { provideValidation } = useValidation(SpotsListValidationErrorsKey)

    const update = ({
      limit = 25,
      newPage = 1
    }: {
      limit?: number
      newPage?: number
    }) => {
      marketplaceStore.SetCurrentPage(newPage)
      marketplaceStore.SetLimit(limit)
      marketplaceStore.FetchSum()
      marketplaceStore.FetchItems()
    }

    const preparePayload = (page: number, withAll?: boolean) => {
      return {
        filter: filter.value,
        sorter: sorter,
        csv: withAll,
        limit: limit.value,
        start: (page - 1) * limit.value
      } as IMarketplaceStatPayload
    }

    const toggleChat = (id?: string, data?: any) => {
      receiverId.value = id ? String(id) : ''
      if (!id) return

      offerDealData.value = {
        ...filter.value,
        hits: data.hits,
        domain: data.domain
      }
    }

    const toggleCampaignWindow = async (id?: string) => {
      if (id) {
        const campaigns = await spotsStore.CheckSuitableCampaigns(id)

        if (campaigns.length === 0) {
          goToCampaigns({ id })
        } else {
          platformId.value = String(id)
        }
      } else {
        platformId.value = ''
      }
    }

    const toggleDealForm = (row: any) => {
      const currentQueryFilters = getQueryFilters() as any

      const query = {
        size_id: row.size_id || 0,
        stream: row.stream_id,
        deal_platform_id: row.platform_id,
        type_id: row.type_id,
        domain: row.domain,
        spot_id: row.spot_id,
        hits: row.hits,
        device_ids: currentQueryFilters.device_ids || [],
        countries_ids: currentQueryFilters.countries_ids || []
      }

      router.push({ path: `/direct_deals/offer-deal/`, query: query })
    }

    const goToCampaigns = ({
      id,
      campaignId
    }: {
      id: string
      campaignId?: string | null
    }) => {
      const platform = marketplaceStore.itemById(id)
      let routeParts: any[] = []
      if (campaignId) {
        routeParts = ['update', campaignId, platform.domain]
      } else {
        routeParts = [
          'new',
          platform.type_id,
          platform.stream_id,
          platform.domain
        ]
        if (platform.type_id === AdType.Banner) {
          routeParts.push(platform.size_id)
        }
      }
      router.push(`/campaigns/${routeParts.join('/')}/`)
    }

    const applySorting = (prop: string, order: string) => {
      marketplaceStore.SetCurrentPage(1)
      marketplaceStore.SetSortField(SORT_PROP_ALIAS[prop])
      marketplaceStore.SetSortDirection(SORT_ALIAS[order])
      update({ newPage: 1 })
    }

    const showSizeColumn = computed(() => {
      return filter.value.type_id === AdType.Banner
    })

    const download = async (format: string) => {
      const items = await marketplaceStore.GetCsv(preparePayload(1, true))
      const data = formatJson(CSV_HEADERS, items)
      if (format === 'csv') {
        exportCSVFile(CSV_HEADERS, data)
      } else {
        exportJson2Excel(CSV_HEADERS, data)
      }
    }

    const listButtonState = computed(() => {
      const rules = [
        {
          condition: !filter.value.stream || !filter.value.type_id,
          tooltip: 'Please filter by a single Stream or Website Type'
        },
        {
          condition: items.value.length === 0,
          tooltip: 'There are no websites for creating a Spot List'
        }
      ]

      const btnState = rules.find((r) => r.condition)
      return btnState
        ? { disabled: true, tooltip: btnState.tooltip }
        : { disabled: false, tooltip: 'Create a new Spot List' }
    })

    const openModal = () => {
      showModal.value = true
    }

    const showReviewMessage = () => {
      const msg = ElMessage({
        type: 'success',
        duration: 10000,
        showClose: true,
        customClass: 'marketplace-success-message',
        message: h('p', { class: 'marketplace-review-message' }, [
          h('span', 'Review'),
          h(
            ElLink,
            {
              type: 'primary',
              underline: true,
              onClick: (e: Event) => {
                e.preventDefault()
                msg.close()
                router.push({ name: 'block_lists' })
              }
            },
            () => 'spot lists'
          ),
          h('span', '?')
        ])
      })
    }

    const onSuccess = () => {
      showModal.value = false
      ElMessage({
        message: 'Spot list successfully created',
        type: 'success',
        duration: 3000,
        customClass: 'marketplace-success-message'
      })
      showReviewMessage()
    }

    const onSubmit = async (name: string) => {
      const payload = {
        ...preparePayload(1, true),
        list_name: name
      }

      await safeSubmitProcessing(
        loading,
        () => marketplaceStore.ExportToSpotsList(payload),
        () => onSuccess(),
        (err) => {
          ElMessage.error({
            message: err.response.data.message || 'Failed to create Spot list',
            duration: 6000
          })
        }
      )
    }

    return {
      loading,
      items,
      total,
      sum,
      page,
      download,
      update,
      filter,
      applySorting,
      receiverId,
      platformId,
      toggleChat,
      toggleCampaignWindow,
      toggleDealForm,
      showSizeColumn,
      goToCampaigns,
      isDesktop,
      openModal,
      showModal,
      onSubmit,
      listButtonState
    }
  }
})
</script>
<style lang="scss" scoped>
.filter-container {
  padding-bottom: 0;
  .el-card__body {
    padding-bottom: 0;
  }
}
.padding-5 {
  padding-top: 5px;
  padding-bottom: 5px;
  margin-bottom: 25px;
}
:deep(.el-card.is-always-shadow) {
  box-shadow: none;
}
:global(.marketplace-review-message) {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #67c23a;
  gap: 4px;
}
:global(.marketplace-success-message) {
  width: 246px;
  display: flex;
  justify-content: space-between;
}
</style>
